import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map, has, findIndex, isObject } from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import ArticleApi from '@/extensions/shop/store/api/article';
import PropertyApi from '@/extensions/shop/store/api/property';
import PropTypeApi from '@/extensions/shop/store/api/propType';
import CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';
import StockApi from '@/extensions/shop/store/api/stock';
import TaxApi from '@/extensions/shop/store/api/tax';
import TagApi from '@/extensions/shop/store/api/tag';
import MenuApi from '@/store/api/cms/menu';

import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

const articlesPaginated = {
    variables: {
        paginationSizes: [25, 50, 100]
    },
    filters: {},
    pageInfo: {
        total: 0,
        perPage: 25,
        currentPage: 1,
        lastPage: 0
    },
    allColumns: [],
    activeColumns: [],
    showFilters: false,
    defaultSort: null,
    link: {},
    items: []
};

const defaultForm = {
    sku: '',
    gtin: '',
    condition: null,
    brand: '',
    status: 'enabled',
    name: '',
    description: '',
    unit: undefined,
    quantity: undefined,
    shipping_points: undefined,
    restock_date: undefined,
    tags: [],
    media: [],
    properties: [],
    prices: [],
    stocks: [],
    coupon: undefined,
    relatedArticles: [],
};

const defaultUi = {
    form: {
        loading: true,
        saving: false,
        initialized: false,
        validateOnEnter: false
    },
    articlesPaginated: {
        loading: true
    }
};

export const useShopArticleStore = defineStore('shopArticle', {
    state: () => ({
        articlesPaginated: cloneDeep(articlesPaginated),
        options: {
            status: {
                loading: false,
                items: [
                    { value: 'enabled', label: 'Enabled' },
                    { value: 'disabled', label: 'Disabled' }
                ]
            },
            condition: {
                loading: false,
                items: [
                    { value: null, label: 'Inherit from product' },
                    { value: 'new', label: 'New' },
                    { value: 'refurbished', label: 'Refurbished' },
                    { value: 'used', label: 'Used' },
                ]
            },
            priceType: {
                loading: false,
                items: [
                    { value: 'main', label: 'Standard' },
                    { value: 'group', label: 'Kundengruppenpreis' },
                    { value: 'sale', label: 'Angebotspreis' },
                    { value: 'scaled', label: 'Mengenrabatt' }
                ]
            },
            propType: {
                loading: false,
                items: []
            },
            property: {
                loading: false,
                items: []
            },
            dateType: {
                loading: false,
                items: [
                    { value: 'datetime', label: 'Datum, Uhrzeit' },
                    { value: 'date', label: 'Datum' },
                    { value: 'week', label: 'Woche' },
                    { value: 'month', label: 'Monat' },
                    { value: 'year', label: 'Jahr' }
                ]
            },
            stockPivotRestriction: {
                loading: false,
                items: [
                    { value: 'anyone', label: 'Anyone' },
                    { value: 'guests', label: 'Guests' },
                    { value: 'customer-groups', label: 'Customer Groups' }
                ]
            },
            customerGroup: {
                loading: false,
                items: []
            },
            stock: {
                loading: false,
                items: []
            },
            tax: {
                loading: false,
                items: []
            },
            tag: {
                loading: false,
                items: []
            },
            menu: {
                loading: false,
                items: []
            }
        },
        form: cloneDeep(defaultForm),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => get(state, 'ui.form.initialized'),
        formValidateOnEnter: (state) => get(state, 'ui.form.validateOnEnter'),
        rules: (state) => {
            const setupStore = useSetupStore(pinia);
            let rules = {
                sku: 'required',
                status: 'required',
                gtin: 'max:14',
                brand: 'max:70',
                name: 'min:3',
                prices: [],
                stocks: []
            };

            const shop = setupStore.activeCms.shop;
            const article = state.form;

            if (article.id) {
                Vue.set(
                    rules,
                    'sku',
                    `required|sku|unique_article_sku:"shop":${shop.id},"excludedArticles":${parseInt(article.id)}`
                );
            } else {
                Vue.set(rules, 'sku', `required|unique_article_sku:"shop":${shop.id}`);
            }

            Vue.set(rules, 'prices', []);

            get(article, 'prices', []).forEach((price, index) => {
                Vue.set(rules.prices, index, {
                    type: 'required',
                    value: 'required|min_value:0',
                    min_qty: 'required|integer|min_value:1',
                    start_at: 'required',
                    end_at: 'required',
                    customerGroup: { id: 'required' }
                });
            });

            Vue.set(rules, 'stocks', []);

            get(article, 'stocks', []).forEach((stock, index) => {
                Vue.set(rules.stocks, index, { id: 'required' });
            });

            return rules;
        }
    },

    actions: {
        addPaginatedArticlesToState(data) {
            this.articlesPaginated.items = data.items;
            this.articlesPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.articlesPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.articlesPaginated.link = cloneDeep(data.config.link);
            this.articlesPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.articlesPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.articlesPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.articlesPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.articlesPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.articlesPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.articlesPaginated.filters, key);
            } else {
                Vue.set(this.articlesPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            Vue.set(this.articlesPaginated.variables, 'order', key);
            Vue.set(this.articlesPaginated.variables, 'direction', value);
        },

        definePaginationSizes(value) {
            Vue.set(this.articlesPaginated.variables, 'paginationSizes', value);
        },

        updatePaginationLoading(value) {
            this.ui.articlesPaginated.loading = value;
        },

        updateFormLoading(value) {
            this.ui.form.loading = value;
        },

        updateFormSaving(value) {
            this.ui.form.saving = value;
        },

        defineFilterVisibility(value) {
            Vue.set(this.articlesPaginated, 'showFilters', value);
        },

        updateRules(rules) {
            // moved to getters
        },

        resetRules() {
            // moved to getters
        },

        resetUI() {
            Vue.set(this, 'ui', cloneDeep(defaultUi));
        },

        insertArticle(article) {
            if (!article.status) {
                article.status = 'enabled';
            }
            Vue.set(this, 'form', article);
        },

        insertArticlesPaginated(data) {
            Vue.set(this, 'articlesPaginated', data);
        },

        clearArticlesPaginated() {
            Vue.set(this, 'articlesPaginated', cloneDeep(articlesPaginated));
        },

        updatePaginatedArticleQty({ article, stock, qty }) {
            const i = this.articlesPaginated.items.findIndex(el => el.id == article.id);
            if (i >= 0) {
                const stocks = cloneDeep(article.stocks) || [];
                let el = stocks.find(el => el.id == stock.id);
                if (!el) stocks.push({ id: stock.id, pivot: { qty } });
                else el.pivot.qty = qty;
                Vue.set(this.articlesPaginated.items[i], 'stocks', stocks);
            }
        },

        replacePaginatedArticle(article) {
            const i = this.articlesPaginated.items.findIndex(el => el.id == article.id);
            if (i >= 0) Vue.set(this.articlesPaginated.items, i, article);
        },

        updatePropTypeOptionsLoading(value) {
            Vue.set(this.options.propType, 'loading', value);
        },

        insertPropTypeOptions(propTypes) {
            Vue.set(this.options.propType, 'items', propTypes);
        },

        updatePropertyOptionsLoading(value) {
            Vue.set(this.options.property, 'loading', value);
        },

        insertPropertyOptions(properties) {
            Vue.set(this.options.property, 'items', properties);
        },

        updateCustomerGroupOptionsLoading(value) {
            Vue.set(this.options.customerGroup, 'loading', value);
        },

        insertCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.customerGroup, 'items', customerGroups);
        },

        updateStockOptionsLoading(value) {
            Vue.set(this.options.stock, 'loading', value);
        },

        insertStockOptions(stocks) {
            Vue.set(this.options.stock, 'items', stocks);
        },

        updateTaxOptionsLoading(value) {
            Vue.set(this.options.tax, 'loading', value);
        },

        insertTaxOptions(taxes) {
            Vue.set(this.options.tax, 'items', taxes);
        },

        updateMenuOptionsLoading(value) {
            Vue.set(this.options.menu, 'loading', value);
        },

        insertMenuOptions(menus) {
            Vue.set(this.options.menu, 'items', menus);
        },

        updateTagOptionsLoading(value) {
            Vue.set(this.options.tag, 'loading', value);
        },

        insertTagOptions(tags) {
            Vue.set(this.options.tag, 'items', tags);
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        addFormMedia(media) {
            const index = get(this, 'form.media.length', 0);
            Vue.set(this.form.media, index, media);
        },

        sortFormMedia(media) {
            const currentMedia = get(this.form, `media`) || [];
            media.forEach((item, index) => {
                const m = findIndex(currentMedia, { id: item.id });
                if (m >= 0) {
                    Vue.set(this.form.media[m], 'position', index + 1);
                }
            });
        },

        markFormMediaAsDeleted(i) {
            Vue.set(this.form.media[i], 'deleted', true);
        },

        unmarkFormMediaAsDeleted(i) {
            Vue.set(this.form.media[i], 'deleted', false);
        },

        updateFormMediaVisibility({ media, scope, value }) {
            const currentMedia = get(this.form, `media`) || [];
            const m = findIndex(currentMedia, { id: media.id });
            if (m >= 0) {
                if (this.form.media[m].settings === undefined) Vue.set(this.form.media[m], 'settings', {});
                if (this.form.media[m].settings.show === undefined) Vue.set(this.form.media[m].settings, 'show', {});
                Vue.set(this.form.media[m].settings.show, scope, value);
            }
        },

        // Art. 50 fields on a media VIDEO. Written here, not on the tile component: the
        // media objects come straight out of the GraphQL result and carry no Vue
        // observer, so assigning on them changes the value but re-renders nothing - the
        // switch stayed put and the badge never lit up. Vue.set against the store's own
        // form array is what makes it reactive, exactly like updateFormMediaVisibility.
        updateFormMediaVideoAi({ media, key, value }) {
            const currentMedia = get(this.form, `media`) || [];
            const m = findIndex(currentMedia, { id: media.id });
            if (m < 0 || !this.form.media[m].video) return;

            Vue.set(this.form.media[m].video, key, value === undefined ? null : value);

            // Turning the disclosure off clears the kind with it, so a video is never
            // left carrying a term it no longer claims.
            if (key === 'ai_disclosure' && value !== true) {
                Vue.set(this.form.media[m].video, 'ai_source_type', null);
            }
        },

        setFormSlots(value) {
            Vue.set(this.form, 'slots', value);
        },

        setFormSku(value) {
            Vue.set(this.form, 'sku', value);
        },

        setFormGtin(value) {
            Vue.set(this.form, 'gtin', value);
        },

        setFormCondition(value) {
            Vue.set(this.form, 'condition', value);
        },

        setFormBrand(value) {
            Vue.set(this.form, 'brand', value);
        },

        setFormStatus(value) {
            Vue.set(this.form, 'status', value);
        },

        setFormName(value) {
            Vue.set(this.form, 'name', value);
        },

        setFormDescription(value) {
            Vue.set(this.form, 'description', value);
        },

        setFormUnit(value) {
            Vue.set(this.form, 'unit', value);
        },

        setFormQuantity(value) {
            Vue.set(this.form, 'quantity', value);
        },

        setFormShippingPoints(value) {
            Vue.set(this.form, 'shipping_points', value);
        },

        setFormRestockDate(value) {
            Vue.set(this.form, 'restock_date', value);
        },

        setFormCouponAmount(value) {
            Vue.set(this.form.coupon, 'amount', value);
        },

        setFormDiscardTheFollowingCoupons(value) {
            Vue.set(this.form.coupon, 'discard_the_following_coupons', value);
        },

        setFormDiscardRemainingAmount(value) {
            Vue.set(this.form.coupon, 'discard_remaining_amount', value);
        },

        setFormTags(tags) {
            Vue.set(this.form, 'tags', tags);
        },

        setFormRelatedArticles(relatedArticles) {
            Vue.set(this.form, 'relatedArticles', relatedArticles);
        },

        addFormPrice(price) {
            const index = get(this, 'form.prices.length', 0);
            if (!price.id) price.id = uniqid('new:');
            Vue.set(this.form.prices, index, price);
        },

        updateFormPrice({ price, field, value }) {
            const prices = get(this.form, 'prices') || [];
            const index = findIndex(prices, { id: price.id });

            switch (field) {
                case 'tax':
                case 'customerGroup':
                    if (!isObject(value)) value = null;
                    break;
                case 'price':
                case 'min_qty':
                    if (isNaN(value)) value = null;
                    break;
            }

            if (index >= 0) {
                Vue.set(this.form.prices[index], field, value);
            }
        },

        deleteFormPrice({ index }) {
            Vue.delete(this.form.prices, index);
        },

        addFormStock(stock) {
            const index = get(this, 'form.stocks.length', 0);
            if (!stock.id) stock.id = uniqid('new:');
            Vue.set(this.form.stocks, index, stock);
        },

        updateFormStock({ index, stock }) {
            const { id, name, status, restriction } = stock;
            if (id !== undefined) Vue.set(this.form.stocks[index], 'id', id);
            if (name !== undefined) Vue.set(this.form.stocks[index], 'name', name);
            if (status !== undefined) Vue.set(this.form.stocks[index], 'status', status);
            if (restriction !== undefined) Vue.set(this.form.stocks[index], 'restriction', restriction);
        },

        updateFormStockPivot({ index, pivot }) {
            const { customerGroups, restriction, sellable_from, sellable_to } = pivot;
            if (customerGroups !== undefined) Vue.set(this.form.stocks[index].pivot, 'customerGroups', customerGroups);
            if (restriction !== undefined) Vue.set(this.form.stocks[index].pivot, 'restriction', restriction);
            if (sellable_from !== undefined) Vue.set(this.form.stocks[index].pivot, 'sellable_from', sellable_from);
            if (sellable_to !== undefined) Vue.set(this.form.stocks[index].pivot, 'sellable_to', sellable_to);
        },

        setFormStockQty({ stock, value }) {
            const stocks = get(this.form, 'stocks') || [];
            const index = findIndex(stocks, { id: stock.id });
            if (index >= 0) {
                Vue.set(this.form.stocks[index].pivot, 'qty', value);
            }
        },

        setFormStockSellableFrom({ stock, value }) {
            const stocks = get(this.form, 'stocks') || [];
            const index = findIndex(stocks, { id: stock.id });
            if (index >= 0) {
                Vue.set(this.form.stocks[index].pivot, 'sellable_from', value);
            }
        },

        setFormStockSellableTo({ stock, value }) {
            const stocks = get(this.form, 'stocks') || [];
            const index = findIndex(stocks, { id: stock.id });
            if (index >= 0) {
                Vue.set(this.form.stocks[index].pivot, 'sellable_to', value);
            }
        },

        deleteFormStock(stock) {
            const stocks = get(this.form, 'stocks') || [];
            const index = findIndex(stocks, { id: stock.id });
            if (index >= 0) {
                Vue.delete(this.form.stocks, index);
            }
        },

        // Async actions
        fetchArticleHistoryPaginated({ stock, articleId, page }) {
            let variables = { stockId: parseInt(stock.id), articleId: parseInt(articleId), page };
            return new Promise((resolve, reject) => {
                ArticleApi.fetchArticleHistoryPaginated(variables).then(data => {
                    resolve(data);
                }).catch((err) => reject(err));
            });
        },

        fetchArticlesPaginated(payload = {}) {
            return ArticleApi.fetchArticlesPaginated({ ...payload, ...this.articlesPaginated }).then(data => {
                this.addPaginatedArticlesToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchArticle({ id, translation }) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            if (cms && cms.shop && cms.shop.id) {
                return ArticleApi.fetchArticle(id, cms.shop.id, translation).then(article => {
                    this.insertArticle(cloneDeep(article));
                });
            }
        },

        fetchPropTypeOptions() {
            this.updatePropTypeOptionsLoading(true);
            return PropTypeApi.fetchList()
                .then(propTypes => {
                    this.updatePropTypeOptionsLoading(false);
                    this.insertPropTypeOptions(propTypes);
                })
                .catch(err => {
                    this.updatePropTypeOptionsLoading(false);
                    throw err;
                });
        },

        fetchPropertyOptions(shopID) {
            this.updatePropertyOptionsLoading(true);
            return PropertyApi.fetchList(shopID)
                .then(properties => {
                    this.updatePropertyOptionsLoading(false);
                    this.insertPropertyOptions(properties);
                })
                .catch(err => {
                    this.updatePropertyOptionsLoading(false);
                    throw err;
                });
        },

        fetchCustomerGroupOptions(shopID) {
            const setupStore = useSetupStore(pinia);
            if (has(setupStore, 'activeCms.shop.defaultCustomerGroup'))
                this.insertCustomerGroupOptions([setupStore.activeCms.shop.defaultCustomerGroup]);

            this.updateCustomerGroupOptionsLoading(true);
            return CustomerGroupApi.fetchList(shopID)
                .then(customerGroups => {
                    this.updateCustomerGroupOptionsLoading(false);
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch(err => {
                    this.updateCustomerGroupOptionsLoading(false);
                    throw err;
                });
        },

        fetchStockOptions(shopID) {
            this.updateStockOptionsLoading(true);
            return StockApi.fetchList(shopID)
                .then(stocks => {
                    this.updateStockOptionsLoading(false);
                    this.insertStockOptions(stocks);
                })
                .catch(err => {
                    this.updateStockOptionsLoading(false);
                    throw err;
                });
        },

        fetchTaxOptions(shopID) {
            const setupStore = useSetupStore(pinia);
            if (has(setupStore, 'activeCms.shop.defaultTax'))
                this.insertTaxOptions([setupStore.activeCms.shop.defaultTax]);

            this.updateTaxOptionsLoading(true);
            return TaxApi.fetchList(shopID)
                .then(taxes => {
                    this.updateTaxOptionsLoading(false);
                    this.insertTaxOptions(taxes);
                })
                .catch(err => {
                    this.updateTaxOptionsLoading(false);
                    throw err;
                });
        },

        fetchMenuOptions(cmsID) {
            this.updateMenuOptionsLoading(true);
            return MenuApi.fetchList(cmsID)
                .then(menus => {
                    this.updateMenuOptionsLoading(false);
                    this.insertMenuOptions(menus);
                })
                .catch(err => {
                    this.updateMenuOptionsLoading(false);
                    throw err;
                });
        },

        fetchTagOptions(cmsID) {
            this.updateTagOptionsLoading(true);
            return TagApi.fetchList(cmsID)
                .then(menus => {
                    this.updateTagOptionsLoading(false);
                    this.insertTagOptions(menus);
                })
                .catch(err => {
                    this.updateTagOptionsLoading(false);
                    throw err;
                });
        },

        propertyAutocomplete({ shopID, propID, valueField, query }) {
            return PropertyApi.autocomplete(shopID, propID, valueField, query);
        },

        create(article) {
            return ArticleApi.create(article).then((article) => {
                this.insertArticle(cloneDeep(article));
            });
        },

        update(article) {
            return ArticleApi.update(article).then(article => {
                this.insertArticle(cloneDeep(article));
            });
        },

        deleteArticle(id) {
            return ArticleApi.delete(id);
        },

        reset() {
            this.insertArticle(cloneDeep(defaultForm));
        }
    }
});
